import { formatLenderConditionTrackingOutline } from "./lender-condition-tracking.js";
import type {
  LoanOfficerBuiltinTemplate,
  LoanOfficerBuiltinTemplateId,
} from "./types.js";

const RATE_NOTES_RULE = `
Rate comparison: Use ONLY rates the broker pasted in chat, rates returned from Velocity deal/lender fields, or explicit numbers the broker supplied. NEVER invent, estimate, or pull live market rates from the web unless the broker asked for a public benchmark and you cite the source. Start the output with: "Notes only — not a rate quote or commitment." Compare apples-to-apples (term, product type, insurer, compounding).`.trim();

export const LOAN_OFFICER_BUILTIN_TEMPLATES: LoanOfficerBuiltinTemplate[] = [
  {
    id: "review_application",
    displayName: "Review application",
    description:
      "Summarize the Velocity deal, key ratios, and gaps before submission.",
    kind: "both",
    suggestedToolPrefixes: ["velocity_", "mortgage_", "drive_"],
    defaultGoalText:
      "Review the named client's mortgage application in Velocity: deal facts, borrowers, property, and outstanding items. Cross-check Drive for application PDFs when a client folder exists. List risks, missing fields, and next steps — do not claim lender approval.",
    defaultFrequencyText: "when I ask in chat",
    skillInstructions: `Application review: Use velocity_get_deal or velocity_search_deals, then mortgage_check_qualification or mortgage_calculate_client_income when income/qualification is in scope. Cite loan codes and amounts from tools only. Flag missing data; never invent borrowers or amounts.`,
  },
  {
    id: "missing_documents",
    displayName: "Missing document list",
    description:
      "AB / Mortgage Architects file readiness and missing doc list from Drive.",
    kind: "both",
    suggestedToolPrefixes: ["mortgage_", "drive_"],
    defaultGoalText:
      "For the client I name, run mortgage_check_file_readiness (and drive_find_client_folder if needed). Return a bullet list of missing documents with Drive links for what is present.",
    defaultFrequencyText: "when I ask in chat",
    skillInstructions: `Missing documents: Always use mortgage_check_file_readiness with clientName (and loanCode when known). Present missing vs complete items with links. Include the internal file-readiness disclaimer — not a compliance sign-off.`,
  },
  {
    id: "draft_borrower_email",
    displayName: "Draft borrower email",
    description:
      "Draft or send borrower emails via connected Gmail (automated assistants auto-send when configured).",
    kind: "both",
    suggestedToolPrefixes: ["gmail_", "velocity_", "drive_"],
    defaultGoalText:
      "When I name a client, draft a professional borrower email about their mortgage file (status, docs needed, or next steps). Use gmail_create_draft unless I asked to send now — then gmail_send_message with confirm:true.",
    defaultFrequencyText: "when I ask in chat",
    skillInstructions: `Borrower email: Use velocity_get_deal for accurate names/loan code. gmail_create_draft for review; gmail_send_message only when the broker or automated assistant goal explicitly says send. Never invent email addresses — use contacts from deal evidence or ask the broker. Wrap paste-ready body in :::copy blocks.`,
  },
  {
    id: "credit_memo",
    displayName: "Credit memo draft",
    description:
      "Internal credit memo outline from deal + income evidence (not a lender submission).",
    kind: "both",
    suggestedToolPrefixes: ["velocity_", "mortgage_", "drive_"],
    defaultGoalText:
      "Draft an internal credit memo for the named file: borrowers, employment, income summary, liabilities, property, LTV, mitigants, and recommendation. Pull numbers from mortgage tools — label as internal working memo, not lender approval.",
    defaultFrequencyText: "when I ask in chat",
    skillInstructions: `Credit memo: Structure with Executive summary, Borrowers, Income (tool-backed), Credit/liabilities, Property, Ratios, Exceptions, Recommendation. Use mortgage_calculate_client_income / mortgage_check_qualification for numbers. State "Internal credit memo — not sent to lender."`,
  },
  {
    id: "explain_loan_terms",
    displayName: "Explain loan terms",
    description:
      "Plain-language explanations of terms on the deal (no invented rates).",
    kind: "chat_skill",
    suggestedToolPrefixes: ["velocity_"],
    defaultGoalText:
      "Explain the loan terms on the named deal in plain language for the borrower (amortization, term, payment type, penalties) using Velocity deal fields only.",
    skillInstructions: `Loan terms: Explain using velocity_get_deal fields only. If a rate is not in evidence, say the broker must confirm — do not guess. Suitable for client education, not legal advice.`,
  },
  {
    id: "summarize_statements",
    displayName: "Summarize statements",
    description:
      "Summarize bank statements or income docs from Drive (deposits, NSFs, payroll).",
    kind: "both",
    suggestedToolPrefixes: ["drive_", "mortgage_"],
    defaultGoalText:
      "Find the client's bank statements or income documents in Drive, read them with drive_get_file, and summarize deposits, payroll, NSFs, and large transfers relevant to mortgage underwriting.",
    defaultFrequencyText: "when I ask in chat",
    skillInstructions: `Statement summary: drive_find_client_folder + drive_search_files + drive_get_file. Summarize patterns; flag NSFs/large transfers. Not a lender income sign-off — broker must verify.`,
  },
  {
    id: "pipeline_update",
    displayName: "Pipeline update",
    description:
      "Scheduled Velocity pipeline digest plus upcoming Calendar meetings.",
    kind: "scheduled_helper",
    suggestedToolPrefixes: ["velocity_", "calendar_"],
    defaultGoalText:
      "Pull active deals from Velocity (velocity_list_deals, last 45 days). Summarize counts by stage, files needing attention, and fundings in the next 14 days. Add calendar_list_upcoming for the next 7 days of client meetings. Write a concise pipeline update for the broker — no approval email.",
    defaultFrequencyText: "every weekday at 8am Calgary",
    defaultSchedule: {
      cronExpression: "@local 0 8 * * 1-5",
      intervalMs: 24 * 60 * 60 * 1000,
      timesPerDay: 1,
      label: "Weekdays at 8:00 AM (Edmonton time)",
    },
    skillInstructions: `Pipeline update (automation): velocity_list_deals + calendar_list_upcoming. Never invent deals. Mention disconnected Velocity/Calendar clearly. Do not ask permission to read connected accounts.`,
  },
  {
    id: "condition_checklist",
    displayName: "Condition checklist",
    description:
      "Track outstanding lender conditions (separate from file-readiness checklist).",
    kind: "both",
    suggestedToolPrefixes: ["velocity_", "drive_", "gmail_"],
    defaultGoalText:
      "For the named deal, list outstanding lender conditions using the condition-tracking template, Velocity status/notes, and any condition emails in Gmail. Output checkboxes the broker can work through.",
    defaultFrequencyText: "when I ask in chat",
    skillInstructions: `Condition checklist:\n${formatLenderConditionTrackingOutline()}\nMerge with velocity_get_deal notes. Mark items missing evidence as "needs broker confirmation".`,
  },
  {
    id: "rate_comparison_notes",
    displayName: "Rate comparison notes",
    description:
      "Compare broker-supplied or Velocity rates — notes only, not a quote.",
    kind: "both",
    suggestedToolPrefixes: ["velocity_"],
    defaultGoalText:
      "Compare the rate options I provide or that appear on the Velocity deal. Output labeled notes only — not a quote.",
    defaultFrequencyText: "when I ask in chat",
    skillInstructions: RATE_NOTES_RULE,
  },
  {
    id: "follow_up_reminder",
    displayName: "Follow-up reminder",
    description:
      "Find stale leads/deals and draft or send follow-ups via Gmail; respect Calendar.",
    kind: "scheduled_helper",
    suggestedToolPrefixes: ["velocity_", "gmail_", "calendar_"],
    defaultGoalText:
      "Check Velocity leads and early-stage deals. For contacts with no broker email in 48+ hours, save a short follow-up as a Gmail draft (gmail_create_draft) for the broker to review; it is only sent automatically when this routine's auto-send setting is on. Skip anyone in automated assistant memory. Cross-check calendar_list_upcoming to reference meeting times. No approval email.",
    defaultFrequencyText: "twice per day on weekdays",
    defaultSchedule: {
      cronExpression: "@local 0 9,15 * * 1-5",
      intervalMs: 12 * 60 * 60 * 1000,
      timesPerDay: 2,
      label: "Weekdays at 9:00 AM and 3:00 PM (Edmonton time)",
    },
    skillInstructions: `Follow-up automation: velocity_list_leads and velocity_list_deals. gmail_list_messages to detect recent outreach. Honor automated assistant memory. calendar_list_upcoming before proposing times. Save follow-ups as drafts; never send unless the routine's auto-send setting is on.`,
  },
  {
    id: "referral_partner_email",
    displayName: "Referral partner email",
    description:
      "Draft emails to realtors, lawyers, or referral partners about a file.",
    kind: "both",
    suggestedToolPrefixes: ["gmail_", "velocity_"],
    defaultGoalText:
      "Draft a professional referral-partner email (realtor/lawyer) about the named deal — status update or doc request. Use gmail_create_draft unless I asked to send.",
    defaultFrequencyText: "when I ask in chat",
    skillInstructions: `Referral partner email: Professional tone, deal status from Velocity, no borrower PII beyond what the broker already shares with partners. :::copy block for the draft.`,
  },
  {
    id: "saved_prompt_templates",
    displayName: "Saved prompt templates",
    description:
      "Save, list, and reuse your own prompt templates (encrypted at rest).",
    kind: "chat_skill",
    suggestedToolPrefixes: [],
    defaultGoalText:
      "Help me save, rename, or apply my loan-officer prompt templates stored in Prime Assistant.",
    skillInstructions: `Saved templates: Direct the broker to Automated assistants → Prompt templates in the app for CRUD. When they paste a template body, help refine it. Do not store secrets in templates.`,
  },
];

const BY_ID: Record<LoanOfficerBuiltinTemplateId, LoanOfficerBuiltinTemplate> =
  Object.fromEntries(
    LOAN_OFFICER_BUILTIN_TEMPLATES.map((t) => [t.id, t]),
  ) as Record<LoanOfficerBuiltinTemplateId, LoanOfficerBuiltinTemplate>;

export function getLoanOfficerBuiltinTemplate(
  id: string,
): LoanOfficerBuiltinTemplate | null {
  return BY_ID[id as LoanOfficerBuiltinTemplateId] ?? null;
}

export function listLoanOfficerBuiltinTemplates(): LoanOfficerBuiltinTemplate[] {
  return [...LOAN_OFFICER_BUILTIN_TEMPLATES];
}

const SKILL_PREFIX = /^\/skill:([a-z0-9_]+)\s*/i;

/** Chat messages may start with `/skill:missing_documents …` */
export function parseLoanOfficerSkillPrefix(
  message: string,
): { skillId: LoanOfficerBuiltinTemplateId; rest: string } | null {
  const m = message.trim().match(SKILL_PREFIX);
  if (!m) {
    return null;
  }
  const id = m[1] as LoanOfficerBuiltinTemplateId;
  if (!BY_ID[id]) {
    return null;
  }
  return { skillId: id, rest: message.trim().slice(m[0].length).trim() };
}

export function resolveLoanOfficerSkillKey(input: {
  message: string;
  helperBuiltinKey?: string | null;
}): LoanOfficerBuiltinTemplateId | null {
  if (input.helperBuiltinKey && BY_ID[input.helperBuiltinKey as LoanOfficerBuiltinTemplateId]) {
    return input.helperBuiltinKey as LoanOfficerBuiltinTemplateId;
  }
  const parsed = parseLoanOfficerSkillPrefix(input.message);
  return parsed?.skillId ?? null;
}

export function loanOfficerSkillInstructions(
  skillId: LoanOfficerBuiltinTemplateId | null,
): string | null {
  if (!skillId) {
    return null;
  }
  return BY_ID[skillId]?.skillInstructions ?? null;
}
